import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

export interface ExplicacaoMetrica {
  titulo: string;
  formula: string;
  descricao: string;
  componentes: Record<string, number | string | null>;
}

interface MetricExplanationTooltipProps {
  explicacao: ExplicacaoMetrica;
  children: React.ReactNode;
}

const fmtVal = (v: number | string | null): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (Math.abs(v) >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
  if (v >= 0 && v <= 1) return `${(v * 100).toFixed(1)}%`;
  return v.toFixed(2);
};

export const MetricExplanationTooltip: React.FC<MetricExplanationTooltipProps> = ({ explicacao, children }) => {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-help">
            {children}
            <Info className="h-3 w-3 text-muted-foreground/60 shrink-0" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs p-3 space-y-2">
          <p className="font-semibold text-xs text-foreground">{explicacao.titulo}</p>
          <p className="text-[11px] text-muted-foreground">{explicacao.descricao}</p>
          <div className="bg-muted/50 rounded px-2 py-1.5">
            <p className="text-[10px] font-mono text-foreground/80">{explicacao.formula}</p>
          </div>
          {Object.keys(explicacao.componentes).length > 0 && (
            <div className="space-y-0.5">
              {Object.entries(explicacao.componentes).map(([key, val]) => (
                <div key={key} className="flex justify-between text-[10px]">
                  <span className="text-muted-foreground">{key}</span>
                  <span className="font-medium tabular-nums">{fmtVal(val)}</span>
                </div>
              ))}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
